// =============================================================================
// HUB IA — les vues (#346 · T5, + Qualité données #370)
// =============================================================================
// DASHBOARD · AGENTS · CAPACITÉS · ÉVÉNEMENTS · VERDICTS · RÈGLES · MONITORING
// + QUALITÉ DONNÉES (#370) — AID-OPP-02 déclinée, avec sa formule de classement
//
// ⚠️ DEUX VUES PORTENT UNE EXIGENCE PRÉCISE DU CHANTIER
//   • ÉVÉNEMENTS montre les événements SANS ABONNÉ. C'est SMELL-05 rendu
//     visible — précédent mesuré : 155 verdicts, 0 tâche ouverte.
//   • VERDICTS montre la DISTRIBUTION DES CINQ. Un verdict constant à 97 %
//     aurait été détecté par cet indicateur dès la première mesure.
//
// CHARTE — zéro couleur en dur. Tout dérive de var(--oh-*), donc tout bascule
// en clair et en foncé. Les blocs colorés utilisent des PAIRES fond/texte
// (--oh-stat-{c}-bg / --oh-stat-{c}-text) : jamais vert sur vert.
//
// L'écran ne recalcule RIEN : il sert ce que le socle mesure. Une vue qui
// referait le calcul de son côté finirait par diverger du moteur, et c'est
// l'écran qui aurait tort en silence.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity, ArrowLeft, Boxes, ClipboardList, Gauge, Layers, ListChecks,
  Radio, RefreshCw, Scale, Sparkles, Zap,
} from "lucide-react";
import { getTenantHeaders } from "../../lib/utils";
import { etatProprieteAgent } from "../../lib/proprieteAgent";

const API = process.env.REACT_APP_BACKEND_URL;

// Les cinq résultats, dans l'ordre de lecture, avec leur paire fond/texte.
const RESULTATS = [
  { cle: "conforme", label: "Conforme", couleur: "green" },
  { cle: "non_conforme", label: "Non conforme", couleur: "red" },
  { cle: "indetermine", label: "Indéterminé", couleur: "amber" },
  { cle: "non_applicable", label: "Non applicable", couleur: "blue" },
  { cle: "erreur_technique", label: "Erreur technique", couleur: "purple" },
];

const MODES = [
  { cle: "off", label: "Off", aide: "ne s'exécute pas" },
  { cle: "manuel", label: "Manuel", aide: "seulement sur demande explicite" },
  { cle: "auto", label: "Auto", aide: "sur événement" },
  { cle: "planifie", label: "Planifié", aide: "périodique" },
];

const VUES = [
  { id: "dashboard", label: "Dashboard", icon: Gauge },
  { id: "agents", label: "Agents", icon: Boxes },
  { id: "capacites", label: "Capacités", icon: Layers },
  { id: "evenements", label: "Événements", icon: Radio },
  { id: "verdicts", label: "Verdicts", icon: ListChecks },
  { id: "regles", label: "Règles", icon: Scale },
  { id: "monitoring", label: "Monitoring", icon: Activity },
  { id: "qualite", label: "Qualité données", icon: ClipboardList },
];

const fond = (c) => `var(--oh-stat-${c}-bg)`;
const texte = (c) => `var(--oh-stat-${c}-text)`;
const bordure = (c) => `var(--oh-stat-${c}-border)`;

async function appeler(chemin, options = {}) {
  const res = await fetch(`${API}/api/hub-ia${chemin}`, {
    headers: { ...getTenantHeaders(), "Content-Type": "application/json" },
    credentials: "include",
    ...options,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} sur ${chemin}`);
  return res.json();
}

// =============================================================================
// Briques d'affichage
// =============================================================================

function Carte({ children, style }) {
  return (
    <div
      style={{
        background: "var(--oh-bg-elevated)",
        border: "0.5px solid var(--oh-border)",
        borderRadius: 12,
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Titre({ children, aide }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>{children}</div>
      {aide && (
        <div style={{ color: "var(--oh-text-muted)", fontSize: 13, lineHeight: 1.6 }}>{aide}</div>
      )}
    </div>
  );
}

function Encart({ couleur = "blue", titre, children }) {
  return (
    <div
      style={{
        background: fond(couleur),
        color: texte(couleur),
        border: `0.5px solid ${bordure(couleur)}`,
        borderRadius: 8,
        padding: 12,
        lineHeight: 1.6,
      }}
    >
      {titre && <div style={{ fontWeight: 500 }}>{titre}</div>}
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  );
}

function Etiquette({ couleur = "blue", children }) {
  return (
    <span
      style={{
        background: fond(couleur),
        color: texte(couleur),
        borderRadius: 8,
        padding: "2px 8px",
        fontSize: 12,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/** La distribution des cinq. Toujours les cinq, même à zéro : une modalité
 *  absente du graphique se lirait « inexistante » au lieu de « jamais vue ». */
function Distribution({ donnees }) {
  const total = donnees?.total || 0;
  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        {RESULTATS.map((r) => {
          const n = donnees?.par_resultat?.[r.cle] ?? 0;
          const pct = donnees?.pourcentages?.[r.cle] ?? 0;
          return (
            <div
              key={r.cle}
              style={{
                background: fond(r.couleur),
                color: texte(r.couleur),
                border: `0.5px solid ${bordure(r.couleur)}`,
                borderRadius: 8,
                padding: 12,
                minWidth: 132,
                flex: "1 1 132px",
              }}
            >
              <div style={{ fontSize: 22, lineHeight: 1.2 }}>{n}</div>
              <div style={{ fontSize: 13 }}>{r.label}</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{pct} %</div>
            </div>
          );
        })}
      </div>
      {total > 0 && donnees?.constant && (
        <Encart couleur="red" titre="Distribution constante">
          {donnees.taux_dominant} % des verdicts valent « {donnees.dominant} ». Un
          indicateur qui ne discrimine pas ne mesure rien — c'est la forme exacte
          des 150 verdicts « incomplet » sur 155.
        </Encart>
      )}
      {total > 0 && !donnees?.constant && (
        <Encart couleur="green" titre="Indicateur discriminant">
          {donnees.modalites_observees} modalités observées sur 5, dominante à{" "}
          {donnees.taux_dominant} %.
        </Encart>
      )}
      {total === 0 && (
        <Encart couleur="blue">Aucun verdict sur la fenêtre choisie.</Encart>
      )}
    </div>
  );
}

function Vide({ children }) {
  return (
    <div style={{ color: "var(--oh-text-muted)", padding: 16, lineHeight: 1.6 }}>{children}</div>
  );
}

function Tableau({ colonnes, lignes, cle }) {
  if (!lignes?.length) return <Vide>Rien à afficher.</Vide>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {colonnes.map((c) => (
              <th
                key={c.cle}
                style={{
                  textAlign: "left",
                  padding: "8px 12px",
                  color: "var(--oh-text-secondary)",
                  fontWeight: 500,
                  borderBottom: "0.5px solid var(--oh-border)",
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={cle ? l[cle] ?? i : i}>
              {colonnes.map((c) => (
                <td
                  style={{
                    padding: "8px 12px",
                    color: "var(--oh-text)",
                    borderBottom: "0.5px solid var(--oh-border)",
                    verticalAlign: "top",
                    lineHeight: 1.6,
                  }}
                  key={c.cle}
                >
                  {c.rendu ? c.rendu(l) : l[c.cle] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// 1 · DASHBOARD
// =============================================================================

function VueDashboard({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const { agents, verdicts, evenements, findings_ouverts, anomalies } = donnees;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
        <Carte>
          <Titre aide="agents enregistrés">Agents</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>{agents?.total ?? 0}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            {agents?.off?.length > 0 && (
              <Etiquette couleur="yellow">{agents.off.length} désactivé(s)</Etiquette>
            )}
            {agents?.en_erreur?.length > 0 && (
              <Etiquette couleur="red">{agents.en_erreur.length} en erreur</Etiquette>
            )}
          </div>
        </Carte>
        <Carte>
          <Titre aide="sur la fenêtre">Verdicts</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>{verdicts?.total ?? 0}</div>
        </Carte>
        <Carte>
          <Titre aide="issus des verdicts non conformes">Constats ouverts</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>{findings_ouverts ?? 0}</div>
        </Carte>
        <Carte>
          <Titre aide="reçus, personne n'écoute">Signaux morts</Titre>
          <div style={{ fontSize: 28, color: "var(--oh-text)" }}>
            {evenements?.nb_signaux_morts ?? 0}
          </div>
        </Carte>
      </div>

      <Carte>
        <Titre aide="les cinq résultats — jamais deux">Distribution des verdicts</Titre>
        <Distribution donnees={verdicts} />
      </Carte>

      <Carte>
        <Titre aide="ce que le Hub signale sur lui-même">Anomalies</Titre>
        {anomalies?.length ? (
          <div style={{ display: "grid", gap: 8 }}>
            {anomalies.map((a) => (
              <Encart key={a.code} couleur="red" titre={a.code}>
                {a.message}
              </Encart>
            ))}
          </div>
        ) : (
          <Encart couleur="green" titre="Aucune anomalie">
            Distribution discriminante, aucun signal mort, sorties consommées.
          </Encart>
        )}
      </Carte>
    </div>
  );
}

// =============================================================================
// 2 · AGENTS (+ gouvernance)
// =============================================================================

/** OMBRE — un ÉTAT lu sur le bus, jamais un mode (#474).
 *
 *  ⛔ Volontairement HORS de `MODES`. Les quatre modes sont un RÉGLAGE que
 *  l'utilisateur choisit ; l'ombre est un CONSTAT sur qui possède la fonction.
 *  En faire un cinquième bouton donnerait à cliquer sur ce qui ne se règle pas
 *  ici — et laisserait croire qu'un agent « en ombre » est mal configuré.
 *
 *  La DÉCISION vit dans `lib/proprieteAgent` — fonction pure, testée sur les
 *  charges utiles réellement rendues par l'API. Ici, rien que du balisage. */
function BadgeOmbre({ propriete }) {
  const vu = etatProprieteAgent(propriete);
  if (!vu.libelle) return null;
  return (
    <Etiquette couleur={vu.couleur}>
      {vu.libelle}
      {vu.detail ? ` — ${vu.detail}` : ""}
    </Etiquette>
  );
}

function VueAgents({ donnees, onChangerMode, occupe }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {donnees.agents?.map(({ descripteur, configuration, indicateurs, derniere_execution, propriete }) => (
        <Carte key={descripteur.code}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ minWidth: 260 }}>
              <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>
                {descripteur.libelle}
              </div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>
                {descripteur.code} · version {descripteur.version}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <Etiquette couleur="blue">{descripteur.famille}</Etiquette>
                <Etiquette couleur="cyan">{descripteur.nature}</Etiquette>
                <Etiquette couleur="purple">priorité {configuration.priorite}</Etiquette>
                {descripteur.en_conflit && (
                  <Etiquette couleur="red">version en conflit</Etiquette>
                )}
                <BadgeOmbre propriete={propriete} />
              </div>
              {propriete?.en_ombre && (
                <div style={{ color: "var(--oh-text-muted)", fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
                  Sur événement, cet agent OBSERVE sans écrire — la fonction
                  appartient au handler historique. Le mode ci-contre reste
                  celui de la gouvernance : il n'y a rien à « remettre sur auto ».
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
              {MODES.map((m) => {
                const actif = configuration.mode === m.cle;
                return (
                  <button
                    key={m.cle}
                    type="button"
                    title={m.aide}
                    disabled={occupe}
                    onClick={() => onChangerMode(descripteur.code, m.cle)}
                    style={{
                      height: 36,
                      padding: "0 12px",
                      borderRadius: 8,
                      cursor: occupe ? "not-allowed" : "pointer",
                      background: actif ? "var(--oh-accent-solid)" : "transparent",
                      color: actif ? "var(--oh-accent-contrast)" : "var(--oh-text-secondary)",
                      border: `1px solid ${actif ? "var(--oh-accent-solid)" : "var(--oh-border)"}`,
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {descripteur.en_conflit && (
            <div style={{ marginTop: 12 }}>
              <Encart couleur="red" titre="L'agent refuse de s'exécuter">
                {descripteur.conflit}
              </Encart>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
              marginTop: 16,
            }}
          >
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Activité</div>
              <div style={{ color: "var(--oh-text)" }}>
                {indicateurs.activite.executions} exécutions ·{" "}
                {indicateurs.activite.non_executions} refusées
              </div>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                {indicateurs.activite.duree_moyenne_ms != null
                  ? `${indicateurs.activite.duree_moyenne_ms} ms en moyenne`
                  : "aucune durée mesurée"}
              </div>
            </div>
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Qualité</div>
              <div style={{ color: "var(--oh-text)" }}>
                {indicateurs.qualite.taux_conclusion} % de conclusion
              </div>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                {indicateurs.qualite.taux_indetermination} % indéterminés ·{" "}
                {indicateurs.qualite.taux_erreur_technique} % erreurs techniques
              </div>
            </div>
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Dernière exécution</div>
              <div style={{ color: "var(--oh-text)", fontSize: 13 }}>
                {derniere_execution?.horodatage
                  ? new Date(derniere_execution.horodatage).toLocaleString("fr-FR")
                  : "jamais"}
              </div>
              {derniere_execution && !derniere_execution.execute && (
                <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                  {derniere_execution.raison_non_execution}
                </div>
              )}
            </div>
            <div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>Sortie</div>
              <div style={{ color: "var(--oh-text)", fontSize: 13 }}>
                {descripteur.evenement_publie || "aucun événement"}
              </div>
              <div style={{ color: "var(--oh-text-secondary)", fontSize: 12 }}>
                consommée par {descripteur.consommateur}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            {/* #484 — cet objet est RECOMPOSÉ à la main : tout champ oublié ici
                est perdu même s'il arrive de l'API. `dominant` et
                `taux_dominant` l'étaient, d'où « dominante à  % ». */}
            <Distribution donnees={{
              total: indicateurs.resultats.verdicts,
              par_resultat: indicateurs.resultats.distribution,
              pourcentages: indicateurs.resultats.pourcentages,
              constant: indicateurs.qualite.constant,
              modalites_observees: indicateurs.qualite.modalites_observees,
              dominant: indicateurs.qualite.dominant,
              taux_dominant: indicateurs.qualite.taux_dominant,
            }} />
          </div>

          {configuration.mode === "off" && (
            <div style={{ marginTop: 12 }}>
              <Encart couleur="yellow" titre="Agent désactivé">
                Il ne s'exécutera dans aucun des quatre modes. Ses verdicts passés
                restent lisibles et datés — désactiver n'est pas supprimer.
              </Encart>
            </div>
          )}
        </Carte>
      ))}
    </div>
  );
}

// =============================================================================
// 3 · CAPACITÉS
// =============================================================================

function VueCapacites({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <Carte>
      <Titre aide="ce que le Hub sait faire — et qui s'en sert d'après l'exécution, pas d'après la déclaration">
        Capacités ({donnees.total})
      </Titre>
      {donnees.declarees_jamais_utilisees?.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Encart couleur="yellow" titre="Déclarées mais jamais appelées">
            {donnees.declarees_jamais_utilisees.join(" · ")} — une capacité déclarée
            requise et jamais exécutée est une déclaration, pas un usage.
          </Encart>
        </div>
      )}
      <Tableau
        cle="code"
        lignes={donnees.capacites}
        colonnes={[
          { cle: "code", label: "Capacité", rendu: (c) => (
            <div>
              <div style={{ color: "var(--oh-text)" }}>{c.libelle}</div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>{c.code} · v{c.version}</div>
            </div>
          ) },
          { cle: "domaine", label: "Domaine", rendu: (c) => <Etiquette couleur="cyan">{c.domaine}</Etiquette> },
          { cle: "fournisseur", label: "Fournisseur unique", rendu: (c) => (
            <span style={{ color: "var(--oh-text-secondary)", fontSize: 12 }}>{c.fournisseur}</span>
          ) },
          { cle: "declaree_par", label: "Déclarée par", rendu: (c) => c.declaree_par?.join(", ") || "—" },
          { cle: "appels", label: "Appels réels", rendu: (c) => (
            c.appels > 0
              ? <Etiquette couleur="green">{c.appels}</Etiquette>
              : <Etiquette couleur="yellow">0</Etiquette>
          ) },
        ]}
      />
    </Carte>
  );
}

// =============================================================================
// 4 · ÉVÉNEMENTS
// =============================================================================

function VueEvenements({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {donnees.nb_signaux_morts > 0 ? (
        <Encart couleur="red" titre={`${donnees.nb_signaux_morts} signal(aux) mort(s)`}>
          Ces événements sont REÇUS et personne ne les écoute. C'est SMELL-05 :
          un producteur, zéro destinataire. Précédent — « document.persiste » a eu
          1 producteur et 0 abonné pendant plusieurs heures, sans que rien ne le dise.
        </Encart>
      ) : (
        <Encart couleur="green" titre="Aucun signal mort">
          Tout événement reçu a au moins un abonné.
        </Encart>
      )}
      <Carte>
        <Titre aide="flux, producteurs, abonnés — et ce que personne n'écoute">
          Flux d'événements
        </Titre>
        <Tableau
          cle="event_type"
          lignes={donnees.evenements}
          colonnes={[
            { cle: "event_type", label: "Événement" },
            { cle: "volume", label: "Reçus" },
            { cle: "handlers_bus", label: "Abonnés bus" },
            { cle: "agents_abonnes", label: "Agents (déclaré)", rendu: (e) => e.agents_abonnes?.join(", ") || "—" },
            // ⚠️ La colonne ci-dessus vient des DESCRIPTEURS : elle dit qui a
            // déclaré consommer. Celle-ci vient du BUS : elle dit qui écrit.
            // Les deux sont montrées côte à côte parce que leur écart est
            // précisément l'information qu'on cherchait (#474).
            { cle: "proprietaire", label: "Propriétaire (bus)", rendu: (e) =>
              e.proprietaire === "hub_ia" ? <Etiquette couleur="green">hub IA</Etiquette>
              : e.proprietaire === "handler_historique" ? (
                e.observation_seule
                  ? <Etiquette couleur="amber">handler · agent en ombre</Etiquette>
                  : <Etiquette couleur="blue">handler historique</Etiquette>
              )
              : e.proprietaire === "conflit" ? <Etiquette couleur="red">deux propriétaires</Etiquette>
              : e.proprietaire === "aucun" ? <Etiquette couleur="red">aucun propriétaire</Etiquette>
              : <span style={{ color: "var(--oh-text-muted)" }}>—</span> },
            { cle: "etat", label: "État", rendu: (e) =>
              e.signal_mort ? <Etiquette couleur="red">signal mort</Etiquette>
              : e.sans_abonne ? <Etiquette couleur="yellow">sans abonné</Etiquette>
              : <Etiquette couleur="green">consommé</Etiquette> },
            { cle: "dernier", label: "Dernier", rendu: (e) =>
              e.dernier ? new Date(e.dernier).toLocaleString("fr-FR") : "—" },
          ]}
        />
      </Carte>
    </div>
  );
}

// =============================================================================
// 5 · VERDICTS
// =============================================================================

function VueVerdicts({ donnees, filtre, onFiltrer }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const couleurDe = (cle) => RESULTATS.find((r) => r.cle === cle)?.couleur || "blue";
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Carte>
        <Titre aide="la distribution des cinq — l'indicateur qui aurait signalé le 97 % constant">
          Distribution
        </Titre>
        <Distribution donnees={donnees.distribution} />
      </Carte>

      <Carte>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => onFiltrer(null)}
            style={{
              height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
              background: !filtre ? "var(--oh-accent-bg)" : "transparent",
              color: !filtre ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
              border: "1px solid var(--oh-border)",
            }}
          >
            Tous
          </button>
          {RESULTATS.map((r) => (
            <button
              key={r.cle}
              type="button"
              onClick={() => onFiltrer(r.cle)}
              style={{
                height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
                background: filtre === r.cle ? fond(r.couleur) : "transparent",
                color: filtre === r.cle ? texte(r.couleur) : "var(--oh-text-secondary)",
                border: `1px solid ${filtre === r.cle ? bordure(r.couleur) : "var(--oh-border)"}`,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <Tableau
          cle="verdict_id"
          lignes={donnees.verdicts}
          colonnes={[
            { cle: "resultat", label: "Résultat", rendu: (v) => (
              <Etiquette couleur={couleurDe(v.resultat)}>
                {RESULTATS.find((r) => r.cle === v.resultat)?.label || v.resultat}
              </Etiquette>
            ) },
            { cle: "regle_code", label: "Règle", rendu: (v) => (
              <div>
                <div style={{ color: "var(--oh-text)" }}>{v.regle_code}</div>
                <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
                  v{v.regle_version} · {v.agent_code} v{v.agent_version}
                </div>
              </div>
            ) },
            { cle: "cible_id", label: "Objet", rendu: (v) => (
              <span style={{ color: "var(--oh-text-secondary)", fontSize: 12 }}>
                {v.cible_type} · {v.cible_id}
              </span>
            ) },
            { cle: "justification", label: "Justification", rendu: (v) => (
              <div style={{ maxWidth: 420 }}>
                <div>{v.justification}</div>
                {v.donnees_evaluees && Object.keys(v.donnees_evaluees).length > 0 && (
                  <div style={{ color: "var(--oh-text-muted)", fontSize: 12, marginTop: 4 }}>
                    lu : {Object.keys(v.donnees_evaluees).join(", ")}
                  </div>
                )}
              </div>
            ) },
            { cle: "horodatage", label: "Quand", rendu: (v) => (
              <span style={{ whiteSpace: "nowrap" }}>
                {new Date(v.horodatage).toLocaleString("fr-FR")}
              </span>
            ) },
          ]}
        />
      </Carte>
    </div>
  );
}

// =============================================================================
// 6 · RÈGLES
// =============================================================================

function VueRegles({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  return (
    <Carte>
      <Titre aide="ajouter un contrôle est une entrée de référentiel, jamais une ligne de code. Une version publiée ne se modifie jamais.">
        Référentiel ({donnees.total}) · domaines : {donnees.domaines?.join(", ") || "—"}
      </Titre>
      <Tableau
        lignes={donnees.regles}
        colonnes={[
          { cle: "code", label: "Règle", rendu: (r) => (
            <div>
              <div style={{ color: "var(--oh-text)" }}>{r.libelle}</div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12 }}>
                {r.code} · v{r.version} · {r.domaine}
              </div>
            </div>
          ) },
          { cle: "tenant_id", label: "Portée", rendu: (r) =>
            r.tenant_id === "*"
              ? <Etiquette couleur="blue">mutualisée</Etiquette>
              : <Etiquette couleur="purple">surcharge {r.tenant_id}</Etiquette> },
          { cle: "validite", label: "Validité", rendu: (r) => (
            <span style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>
              {r.date_debut_validite || "—"} → {r.date_fin_validite || "sans fin"}
            </span>
          ) },
          { cle: "en_vigueur", label: "État", rendu: (r) =>
            r.en_vigueur
              ? <Etiquette couleur="green">en vigueur</Etiquette>
              : <Etiquette couleur="yellow">hors vigueur</Etiquette> },
          { cle: "gravite", label: "Gravité" },
          { cle: "application", label: "Domaine d'application", rendu: (r) => (
            <code style={{ fontSize: 11, color: "var(--oh-text-secondary)" }}>
              {JSON.stringify(r.application || {})}
            </code>
          ) },
        ]}
      />
    </Carte>
  );
}

// =============================================================================
// 7 · MONITORING
// =============================================================================

function VueMonitoring({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const t = donnees.temps_execution_ms || {};
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
        <Carte>
          <Titre aide="médiane · p90 · max">Temps d'exécution</Titre>
          <div style={{ color: "var(--oh-text)", fontSize: 22 }}>{t.median ?? "—"} ms</div>
          <div style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
            p90 {t.p90 ?? "—"} ms · max {t.max ?? "—"} ms
          </div>
        </Carte>
        <Carte>
          <Titre aide="défauts du Hub, pas des dossiers">Erreurs techniques</Titre>
          <div style={{ color: "var(--oh-text)", fontSize: 22 }}>
            {donnees.taux_erreur_technique} %
          </div>
        </Carte>
        <Carte>
          <Titre aide="runs batch interrompus, repris au dernier objet traité">
            Files en attente
          </Titre>
          <div style={{ color: "var(--oh-text)", fontSize: 22 }}>
            {donnees.nb_files_en_attente}
          </div>
        </Carte>
      </div>
      <Carte>
        <Titre aide="reprise exacte : le curseur est le dernier identifiant traité">
          Runs batch reprenables
        </Titre>
        <Tableau
          cle="run_id"
          lignes={donnees.files_en_attente}
          colonnes={[
            { cle: "agent_code", label: "Agent" },
            { cle: "statut", label: "Statut" },
            { cle: "traites", label: "Traités" },
            { cle: "curseur", label: "Curseur" },
            { cle: "run_id", label: "Run" },
          ]}
        />
      </Carte>
    </div>
  );
}

// =============================================================================
// 8 · QUALITÉ DES DONNÉES — AID-OPP-02 déclinée (#370)
// =============================================================================

function VueQualite({ donnees }) {
  if (!donnees) return <Vide>Chargement…</Vide>;
  const f = donnees.formule_priorite || {};
  const a = donnees.actionnable_maintenant || {};
  const ind = donnees.non_actionnable_encore || {};
  const max = Math.max(1, ...(donnees.domaines || []).map((d) => d.impact || 0));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* Ce qui est actionnable SANS saisir une seule donnée passe en premier. */}
      <Carte style={{ borderColor: bordure("amber") }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ background: fond("amber"), color: texte("amber"),
                        borderRadius: 8, padding: 8, display: "flex" }}>
            <Sparkles size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>
              {a.opportunites_certaines ?? 0} opportunités certaines sur{" "}
              {a.dossiers_avec_opportunite ?? 0} dossiers
            </div>
            <div style={{ color: "var(--oh-text-muted)", fontSize: 13, lineHeight: 1.6 }}>
              Actionnables immédiatement — aucune donnée à compléter. À traiter
              avant tout domaine ci-dessous.
            </div>
          </div>
        </div>
      </Carte>

      <Carte>
        <Titre aide="indicateur de qualité de données — pas un indicateur de performance opérateur">
          Domaines à compléter ({donnees.dossiers_examines} dossiers examinés)
        </Titre>

        {/* ⚠️ LA FORMULE, À CÔTÉ DU CLASSEMENT. */}
        <div style={{ marginBottom: 12 }}>
          <Encart couleur="blue" titre="Comment ce classement est calculé">
            <code style={{ fontSize: 12 }}>{f.expression}</code>
            <div style={{ marginTop: 4 }}>{f.explication}</div>
          </Encart>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {(donnees.domaines || []).map((d) => (
            <div key={d.champ} style={{
              border: "0.5px solid var(--oh-border)", borderRadius: 8, padding: 12,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            gap: 12, flexWrap: "wrap" }}>
                <span style={{ color: "var(--oh-text)", lineHeight: 1.6 }}>{d.libelle}</span>
                <span style={{ color: "var(--oh-text-secondary)", fontSize: 13 }}>
                  {d.dossiers_sans_la_donnee} dossiers ({d.part} %) × {d.nb_aides_debloquees} aide(s)
                  {" = "}
                  <strong style={{ color: "var(--oh-accent-text)" }}>impact {d.impact}</strong>
                </span>
              </div>
              <div style={{ marginTop: 8, height: 6, borderRadius: 8,
                            background: "var(--oh-bg)", overflow: "hidden" }}>
                <div style={{ width: `${Math.round(100 * (d.impact || 0) / max)}%`,
                              height: "100%", background: "var(--oh-accent-solid)" }} />
              </div>
              <div style={{ color: "var(--oh-text-muted)", fontSize: 12, marginTop: 6 }}>
                débloquerait : {(d.aides_dependantes || []).join(" · ")}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12 }}>
          {/* Le double compte est dit, pas caché. */}
          <Encart couleur="slate" titre={`${donnees.dossiers_incomplets_distincts} dossiers incomplets (effectif distinct)`}>
            {donnees.explication_double_compte}
          </Encart>
        </div>
      </Carte>

      {/* ⚠️ RANG 3 — SÉPARÉ DES DOMAINES, ET APRÈS EUX.
          Un domaine dit « quelle donnée compléter » ; une indétermination dit
          « sur quelle aide on ne peut pas encore conclure ». Les mêler ferait
          lire une absence de savoir comme un manquement du dossier. */}
      <Carte>
        <Titre aide="ni actionnable aujourd'hui, ni un manquement du dossier">
          Situations non encore actionnables
        </Titre>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ background: fond("blue"), color: texte("blue"),
                        border: `0.5px solid ${bordure("blue")}`,
                        borderRadius: 8, padding: 12, minWidth: 160, flex: "1 1 160px" }}>
            <div style={{ fontSize: 22, lineHeight: 1.2 }}>
              {ind.aides_indeterminees ?? 0}
            </div>
            <div style={{ fontSize: 13 }}>aides à l'éligibilité indéterminée</div>
          </div>
          <div style={{ background: fond("blue"), color: texte("blue"),
                        border: `0.5px solid ${bordure("blue")}`,
                        borderRadius: 8, padding: 12, minWidth: 160, flex: "1 1 160px" }}>
            <div style={{ fontSize: 22, lineHeight: 1.2 }}>
              {ind.dossiers_avec_indetermination ?? 0}
            </div>
            <div style={{ fontSize: 13 }}>dossiers concernés</div>
          </div>
        </div>
      </Carte>
    </div>
  );
}


// =============================================================================
// L'ÉCRAN
// =============================================================================

export default function HubIA({ onClose }) {
  const [vue, setVue] = useState("dashboard");
  const [donnees, setDonnees] = useState({});
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [filtreResultat, setFiltreResultat] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const q = filtreResultat ? `?resultat=${filtreResultat}` : "";
      const [dashboard, agents, capacites, evenements, verdicts, regles, monit] =
        await Promise.all([
          appeler("/dashboard"),
          appeler("/agents"),
          appeler("/capacites"),
          appeler("/evenements"),
          appeler(`/verdicts${q}`),
          appeler("/regles"),
          appeler("/monitoring"),
        ]);
      const qualite = await appeler("/qualite-donnees").catch(() => null);
      setDonnees({ dashboard, agents, capacites, evenements, verdicts, regles,
                   monitoring: monit, qualite });
    } catch (e) {
      setErreur(e.message || "Erreur de chargement");
    } finally {
      setChargement(false);
    }
  }, [filtreResultat]);

  useEffect(() => {
    charger();
  }, [charger]);

  const changerMode = useCallback(
    async (agentCode, mode) => {
      setOccupe(true);
      try {
        await appeler(`/agents/${agentCode}/gouvernance`, {
          method: "PUT",
          body: JSON.stringify({ mode, par: "hub-ia-ui" }),
        });
        await charger();
      } catch (e) {
        setErreur(e.message);
      } finally {
        setOccupe(false);
      }
    },
    [charger]
  );

  const contenu = useMemo(() => {
    switch (vue) {
      case "agents":
        return <VueAgents donnees={donnees.agents} onChangerMode={changerMode} occupe={occupe} />;
      case "capacites":
        return <VueCapacites donnees={donnees.capacites} />;
      case "evenements":
        return <VueEvenements donnees={donnees.evenements} />;
      case "verdicts":
        return (
          <VueVerdicts
            donnees={donnees.verdicts}
            filtre={filtreResultat}
            onFiltrer={setFiltreResultat}
          />
        );
      case "regles":
        return <VueRegles donnees={donnees.regles} />;
      case "monitoring":
        return <VueMonitoring donnees={donnees.monitoring} />;
      case "qualite":
        return <VueQualite donnees={donnees.qualite} />;
      default:
        return <VueDashboard donnees={donnees.dashboard} />;
    }
  }, [vue, donnees, filtreResultat, changerMode, occupe]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--oh-bg)" }}>
      <div
        style={{
          borderBottom: "0.5px solid var(--oh-border)",
          background: "var(--oh-bg-surface)",
          padding: "16px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Revenir à l'accueil"
              style={{
                height: 36, width: 36, borderRadius: 8, cursor: "pointer",
                background: "transparent", color: "var(--oh-text-secondary)",
                border: "1px solid var(--oh-border)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <Zap size={20} style={{ color: "var(--oh-accent-text)" }} />
          <div>
            <div style={{ color: "var(--oh-text)", fontWeight: 500, lineHeight: 1.6 }}>Hub IA</div>
            <div style={{ color: "var(--oh-text-muted)", fontSize: 13 }}>
              Agents, capacités, règles et verdicts
            </div>
          </div>
          <button
            type="button"
            onClick={charger}
            disabled={chargement}
            aria-label="Rafraîchir les données"
            style={{
              marginLeft: "auto", height: 36, padding: "0 12px", borderRadius: 8,
              cursor: chargement ? "not-allowed" : "pointer",
              background: "transparent", color: "var(--oh-text-secondary)",
              border: "1px solid var(--oh-border)",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <RefreshCw size={14} /> Rafraîchir
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          {VUES.map((v) => {
            const Icone = v.icon;
            const actif = vue === v.id;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setVue(v.id)}
                style={{
                  height: 36, padding: "0 12px", borderRadius: 8, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 8,
                  background: actif ? "var(--oh-accent-bg)" : "transparent",
                  color: actif ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
                  border: `1px solid ${actif ? "var(--oh-accent)" : "var(--oh-border)"}`,
                }}
              >
                <Icone size={14} /> {v.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: 24 }}>
        {erreur && (
          <div style={{ marginBottom: 16 }}>
            <Encart couleur="red" titre="Erreur de chargement">
              {erreur}
            </Encart>
          </div>
        )}
        {chargement && !Object.keys(donnees).length ? <Vide>Chargement…</Vide> : contenu}
      </div>
    </div>
  );
}
